import { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface PageShellProps {
  /**
   * Optional header toolbar (page title, filters, primary action). It scrolls away normally — only
   * the TabCardPanel's tab bar pins to the top. Pass `undefined`/omit to render an empty spacer row,
   * matching the standard `py-0.5` page header.
   */
  toolbar?: ReactNode;
  /** Page content — typically a `TabCardPanel` (or a loading / empty state). */
  children: ReactNode;
  /** Extra classes for the content area (defaults to `space-y-6`). */
  contentClassName?: string;
}

/**
 * Standard top-level page shell: a full-height column with a header toolbar above the content.
 *
 * IMPORTANT (#5348): the content area is intentionally NOT `flex-1`. A fixed-height flex child gives
 * the content a constrained box, which breaks `position: sticky` on the `TabCardPanel` tab bar — the
 * row can't pin to the page scroller. Letting the content flow naturally means the page scrolls in
 * `<main>` and the sticky tab bar + Save button stay visible. Use this for every TabCardPanel page
 * instead of hand-rolling `<div className="flex flex-col h-full"> ... <div className="flex-1"> ...`.
 */
export function PageShell({ toolbar, children, contentClassName }: PageShellProps) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between py-0.5">{toolbar}</div>
      <div className={cn('space-y-6', contentClassName)}>{children}</div>
    </div>
  );
}
